import React from 'react'
import { Image, Pressable, Text, View } from 'react-native'

import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { multiSelect, selectOption } from '@ui-kit/components/formComponents/MultiSelect/styles'
import { COLOR } from '@ui-kit/constants'
import { caption } from '@ui-kit/core/typography'

import type {
  MultiSelectOptionLeading,
  MultiSelectOptionValue,
  MultiSelectProps,
  SelectOptionProps,
} from './types'

const OptionLeading = ({ leading }: { leading: MultiSelectOptionLeading }) => {
  if (leading.type === 'icon') {
    const { type: _type, ...iconProps } = leading
    return <PhosphorIcon size={14} color={COLOR.black} weight="bold" {...iconProps} />
  }

  const source = typeof leading.source === 'string' ? { uri: leading.source } : leading.source

  return <Image className={selectOption().leadingImage()} resizeMode="contain" source={source} />
}

const SelectOption = (props: SelectOptionProps) => {
  const { container, text, leading } = selectOption({
    isChecked: props.isSelected,
    inverted: props.inverted,
    hasLeading: Boolean(props.leading),
  })

  return (
    <Pressable testID={props.testID} className={container()} onPress={props.onPress}>
      {props.leading ? (
        <View className={leading()}>
          <OptionLeading leading={props.leading} />
        </View>
      ) : null}
      <Text className={text()}>{props.label}</Text>
    </Pressable>
  )
}

const MultiSelect = (props: MultiSelectProps) => {
  const { container } = multiSelect()

  const handleOnPressItem = async (
    changedOptionValue: MultiSelectOptionValue,
    changedOptionIndex: number
  ) => {
    const currentValue = props.value || []
    const selectedValueIndex = currentValue.indexOf(changedOptionValue)

    const newValue =
      selectedValueIndex !== -1
        ? currentValue.filter((value) => value !== changedOptionValue)
        : [...currentValue, changedOptionValue]

    await props.onValueChange?.(newValue, changedOptionIndex)
  }

  return (
    <View>
      <View className={container()}>
        {props.options.map((option, index) => {
          const isOptionSelected = props.value?.includes(option.value) ?? false
          return (
            <SelectOption
              key={String(option.value)}
              testID={`multiselect-option-${option.value}`}
              label={option.label}
              leading={option.leading}
              onPress={() => handleOnPressItem(option.value, index)}
              isSelected={isOptionSelected}
              inverted={props.inverted}
            />
          )
        })}
      </View>
      {props.errorMessage ? (
        <Text className={caption({ color: 'error' })}>{props.errorMessage}</Text>
      ) : null}
    </View>
  )
}

export default MultiSelect
